import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { PillChip } from "@/components/toggles";
import { Heart, ArrowLeft, SlidersHorizontal, Bell, X, Lock } from "lucide-react";

const AREAS = [
  { value: "MARKETPLACE", label: "Marketplace" },
  { value: "STILLTOUCHEDX", label: "stilltouchedX" },
];

export default function WatchlistPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [area, setArea] = useState("MARKETPLACE"); // iter44: nur MP + STX hier
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [sortBy, setSortBy] = useState("newest");
  const [showFilters, setShowFilters] = useState(false);
  const [prefsFor, setPrefsFor] = useState(null);
  const [unreadByTarget, setUnreadByTarget] = useState({});

  const SORTS = [
    { value: "newest", label: t("watchlist.sort_newest") },
    { value: "price_asc", label: t("watchlist.sort_price_asc") },
    { value: "price_desc", label: t("watchlist.sort_price_desc") },
  ];
  const STATUSES = [
    { value: "", label: t("watchlist.status_all") },
    { value: "available", label: t("watchlist.status_available") },
    { value: "sold_out", label: t("watchlist.status_sold_out") },
  ];
  const LISTING_PREF_LABELS = {
    price_drop: t("watchlist.pref_price_drop"),
    status_change: t("watchlist.pref_status_change"),
    low_stock: t("watchlist.pref_low_stock"),
    ending_soon: t("watchlist.pref_ending_soon"),
  };
  const POST_PREF_LABELS = {
    post_updated: t("watchlist.pref_post_updated"),
    post_deleted: t("watchlist.pref_post_deleted"),
  };

  // iter44: SOCIAL wurde ausgegliedert nach /saved-posts
  const isSocial = false;

  const loadUnread = useCallback(async () => {
    try {
      const { data } = await api.get("/me/watch-notifications/summary");
      setUnreadByTarget(data.by_target || {});
    } catch { /* ignore */ }
  }, []);

  const load = useCallback(async () => {
    if (!user || user === false) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ area, sort_by: sortBy });
      if (!isSocial && status) params.append("status", status);
      const { data } = await api.get(`/profile/watchlist/enhanced?${params}`);
      setListings(data.listings || []);
    } catch { setListings([]); }
    finally { setLoading(false); }
  }, [user, area, sortBy, status, isSocial]);

  useEffect(() => {
    if (!user || user === false) { navigate("/login"); return; }
    load();
    loadUnread();
  }, [user, navigate, load, loadUnread]);

  const unwatch = async (item) => {
    try {
      const url = item.target_type === "POST"
        ? `/posts/${item.target_id}/watch`
        : `/listings/${item.target_id}/watch`;
      await api.post(url);
      setListings(prev => prev.filter(l => l.target_id !== item.target_id));
    } catch { /* ignore */ }
  };

  const togglePref = async (item, key, currentValue) => {
    try {
      const { data } = await api.patch(`/me/watches/${item.target_id}/preferences`, {
        preferences: { [key]: !currentValue },
      });
      setListings(prev => prev.map(l => l.target_id === item.target_id ? { ...l, watch_preferences: data.preferences } : l));
    } catch { /* ignore */ }
  };

  const openItem = async (item) => {
    // iter42: Mark related WATCH_* notifications as read then navigate.
    try {
      await api.post(`/me/watch-notifications/mark-read/${item.target_id}`);
      setUnreadByTarget(prev => { const nx = { ...prev }; delete nx[item.target_id]; return nx; });
      window.dispatchEvent(new CustomEvent("watch-unread-refresh"));
    } catch { /* ignore */ }
    if (item.target_type === "POST") {
      navigate(`/post/${item.target_id}`);
    } else if (item.area_scope === "MARKETPLACE") {
      navigate(`/marketplace/${item.target_id}`);
    } else {
      navigate(`/stilltouchedx/${item.target_id}`);
    }
  };

  const PREF_LABELS = isSocial ? POST_PREF_LABELS : LISTING_PREF_LABELS;

  return (
    <div data-testid="watchlist-page">
      <div className="flex items-center gap-3 px-4 py-3 border-b border-[#E5E5E5]">
        <button data-testid="watchlist-back" onClick={() => navigate(-1)} className="text-[#0A0A0A]"><ArrowLeft size={20} /></button>
        <h1 className="text-base font-bold text-[#0A0A0A] flex-1">{t("watchlist.title")}</h1>
        {!isSocial && (
          <button data-testid="watchlist-filter-toggle" onClick={() => setShowFilters(!showFilters)} className="p-1 text-[#666]">
            <SlidersHorizontal size={18} />
          </button>
        )}
        <span className="text-xs text-[#999]">{listings.length}</span>
      </div>

      {/* Bereich-Tabs */}
      <div data-testid="watchlist-tabs" className="flex border-b border-[#E5E5E5] px-1">
        {AREAS.map((a) => (
          <button
            key={a.value}
            data-testid={`wl-tab-${a.value}`}
            onClick={() => { setArea(a.value); setShowFilters(false); setPrefsFor(null); }}
            className={`flex-1 py-3 text-xs font-medium border-b-2 transition-colors ${area === a.value ? "border-[#0A0A0A] text-[#0A0A0A]" : "border-transparent text-[#999]"}`}>
            {a.label}
          </button>
        ))}
      </div>

      {/* Filters – nur bei Marketplace + stilltouchedX */}
      {!isSocial && showFilters && (
        <div data-testid="watchlist-filters" className="px-4 py-3 border-b border-[#E5E5E5] space-y-3 bg-[#FAFAFA]">
          <div>
            <p className="text-[10px] text-[#999] uppercase tracking-wider font-medium mb-1">{t("watchlist.filter_status")}</p>
            <div className="flex gap-1.5 flex-wrap">
              {STATUSES.map((s) => (
                <PillChip
                  key={s.value}
                  testId={`wl-status-${s.value || "all"}`}
                  active={status === s.value}
                  onClick={() => setStatus(s.value)}
                  variant="neutral"
                  size="sm"
                >
                  {s.label}
                </PillChip>
              ))}
            </div>
          </div>
          <div>
            <p className="text-[10px] text-[#999] uppercase tracking-wider font-medium mb-1">{t("watchlist.filter_sort")}</p>
            <div className="flex gap-1.5 flex-wrap">
              {SORTS.map((s) => (
                <PillChip
                  key={s.value}
                  testId={`wl-sort-${s.value}`}
                  active={sortBy === s.value}
                  onClick={() => setSortBy(s.value)}
                  variant="neutral"
                  size="sm"
                >
                  {s.label}
                </PillChip>
              ))}
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-16"><div className="w-6 h-6 border-2 border-[#E5E5E5] border-t-[#0A0A0A] rounded-full animate-spin" /></div>
      ) : listings.length === 0 ? (
        <div className="px-4 py-16 text-center">
          <Heart size={32} className="mx-auto text-[#E5E5E5] mb-3" />
          <p className="text-sm text-[#999]">
            {isSocial ? t("watchlist.empty_social") : t("watchlist.empty_area")}
          </p>
        </div>
      ) : (
        <div className="divide-y divide-[#E5E5E5]">
          {listings.map((item) => {
            const prefs = item.watch_preferences || (isSocial ? { post_updated: true, post_deleted: true } : { price_drop: true, status_change: true, low_stock: true, ending_soon: true });
            const soldOut = !isSocial && (item.listing_status === "SOLD_OUT" || item.listing_status === "SOLD");
            const isPrefsOpen = prefsFor === item.target_id;
            const badge = unreadByTarget[item.target_id];
            // Iter77b: intelligenter AVS-Blur — nur wenn Titelbild ein Trage-Foto ist
            const isStx = item.area_scope === "STILLTOUCHEDX";
            const isAvsVerified = user?.age_verification_status === "VERIFIED";
            const wornSetW = new Set([...(item.worn_photo_urls || []), ...(item.body_content_urls || [])]);
            const titleUrlW = item.title_photo_url || (item.media_urls || [])[0] || item.media_url;
            const titleIsWornW = titleUrlW && wornSetW.has(titleUrlW);
            const needsBlur = isStx && titleIsWornW && !isAvsVerified;
            return (
              <div key={item.target_id} data-testid={`watchlist-item-${item.target_id}`}
                className="relative flex gap-3 px-4 py-3 cursor-pointer hover:bg-[#FAFAFA] transition-colors"
                onClick={() => openItem(item)}>
                <div className="w-16 h-20 bg-[#F5F5F5] shrink-0 overflow-hidden relative">
                  {titleUrlW ? (
                    <img
                      src={titleUrlW}
                      alt=""
                      className={`w-full h-full object-contain bg-[#0A0A0A] transition ${needsBlur ? "blur-lg opacity-60" : ""}`}
                      data-testid={needsBlur ? `watchlist-blurred-${item.target_id}` : undefined}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-[10px] text-[#CCC]">
                      {isSocial ? t("watchlist.text_post") : t("watchlist.no_image")}
                    </div>
                  )}
                  {needsBlur && (
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <Lock size={16} className="text-white drop-shadow" />
                    </div>
                  )}
                  {badge > 0 && (
                    <span data-testid={`watchlist-row-badge-${item.target_id}`}
                      className="absolute -top-1 -left-1 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                      {badge > 9 ? "9+" : badge}
                    </span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  {isSocial ? (
                    <>
                      <p className="text-sm font-medium text-[#0A0A0A] truncate">{item.creator_display_name || "@" + (item.creator_handle || t("watchlist.unknown_creator"))}</p>
                      <p className="text-xs text-[#666] mt-0.5 line-clamp-2">{item.text || item.title || "—"}</p>
                    </>
                  ) : (
                    <>
                      <p className="text-sm font-medium text-[#0A0A0A] truncate">{item.title}</p>
                      <div className="flex gap-1.5 mt-0.5">
                        <span className="text-[10px] px-1.5 py-0.5 bg-[#F5F5F5] text-[#666]">{item.area_scope === "MARKETPLACE" ? "MP" : "STX"}</span>
                        {soldOut && <span className="text-[10px] px-1.5 py-0.5 bg-red-50 text-red-500">{t("watchlist.sold_out_label")}</span>}
                        {!soldOut && item.quantity != null && item.quantity <= 2 && (
                          <span className="text-[10px] px-1.5 py-0.5 bg-amber-50 text-amber-700">{t("watchlist.only_left", { n: item.quantity })}</span>
                        )}
                      </div>
                      <p className="text-sm font-bold text-[#0A0A0A] mt-1">{item.price?.toFixed(2)} {item.currency}</p>
                    </>
                  )}
                </div>
                <div className="flex items-center gap-1 shrink-0 self-center">
                  <button data-testid={`watchlist-prefs-${item.target_id}`}
                    onClick={(e) => { e.stopPropagation(); setPrefsFor(isPrefsOpen ? null : item.target_id); }}
                    aria-label={t("header.notifications")}
                    title={t("header.notifications")}
                    className="p-1 text-[#666] hover:text-[#0A0A0A]">
                    <Bell size={16} />
                  </button>
                  <button data-testid={`watchlist-remove-${item.target_id}`}
                    onClick={(e) => { e.stopPropagation(); unwatch(item); }}
                    className="p-1 text-[#0A0A0A]">
                    <Heart size={18} className="fill-[#0A0A0A]" />
                  </button>
                </div>
                {isPrefsOpen && (
                  <div
                    data-testid={`watchlist-prefs-popover-${item.target_id}`}
                    onClick={(e) => e.stopPropagation()}
                    className="absolute right-4 top-14 w-64 bg-white border border-[#E5E5E5] shadow-lg z-10 rounded">
                    <div className="flex items-center justify-between px-3 py-2 border-b border-[#E5E5E5]">
                      <p className="text-xs font-medium text-[#0A0A0A]">{t("watchlist.notify_title")}</p>
                      <button onClick={() => setPrefsFor(null)} className="text-[#999] hover:text-[#0A0A0A]"><X size={14} /></button>
                    </div>
                    <div className="divide-y divide-[#F0F0F0]">
                      {Object.entries(PREF_LABELS).map(([key, label]) => (
                        <label key={key} className="flex items-center justify-between px-3 py-2 cursor-pointer hover:bg-[#FAFAFA]">
                          <span className="text-xs text-[#333]">{label}</span>
                          <input
                            data-testid={`watchlist-prefs-${key}-${item.target_id}`}
                            type="checkbox"
                            checked={!!prefs[key]}
                            onChange={() => togglePref(item, key, prefs[key])}
                            className="w-4 h-4 accent-[#0A0A0A]"
                          />
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
